import { useEffect, useMemo, useState } from "react";

import { Link, NavLink, Routes, Route } from "react-router-dom";
import { useAuth } from "@/lib/AuthProvider";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, CalendarDays, Users, Stethoscope, MessageSquare, LogOut, Settings, ShoppingBag, ClipboardList, Menu } from "lucide-react";
import { useState as useLocalState } from "react";

function Sidebar({ user, logout }) {
  const links = [
    { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true },
    { to: "/admin/consultations", icon: ClipboardList, label: "Consultations" },
    { to: "/admin/farm-visits", icon: ClipboardList, label: "Farm Visits" },
    { to: "/admin/calendar", icon: CalendarDays, label: "Calendar" },
    { to: "/admin/appointments", icon: CalendarDays, label: "Appointments" },
    { to: "/admin/services", icon: Stethoscope, label: "Services" },
    { to: "/admin/products", icon: ShoppingBag, label: "Shop / Products" },
    { to: "/admin/clients", icon: Users, label: "Clients" },
    { to: "/admin/messages", icon: MessageSquare, label: "Messages" },
    { to: "/admin/settings", icon: Settings, label: "Site Settings" },
  ];
  return (
    <aside className="lg:w-64 lg:min-h-screen lg:border-r lg:border-[#EAD9B0] bg-white flex-shrink-0 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto" data-testid="admin-sidebar">
      <div className="p-4 md:p-6 border-b border-[#EAD9B0] flex items-center gap-3">
        <img src="/logo.png" alt="" className="h-12 md:h-14" />
        <div>
          <div className="font-display text-base leading-none">Jay's Vet</div>
          <div className="text-[10px] uppercase tracking-widest text-[#0F766E] mt-1 font-semibold">Admin</div>
        </div>
      </div>
      <nav className="p-2 md:p-3 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={`sidebar-${l.label.toLowerCase().replace(/\s|\//g, "-")}`}
            className={({ isActive }) => `flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs md:text-sm whitespace-nowrap ${isActive ? "bg-[#0F766E] text-white" : "text-[#1C1917] hover:bg-[#FFF7E6]"}`}>
            <l.icon size={16} /> <span className="hidden sm:inline lg:inline">{l.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="hidden lg:block p-4 border-t border-[#EAD9B0] mt-4">
        <div className="text-xs text-[#57534E]">Signed in as</div>
        <div className="text-sm font-medium truncate">{user.email}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#0F766E] mt-1 font-semibold">{user.role}</div>
        <button onClick={logout} className="mt-3 text-xs text-[#57534E] hover:text-[#1C1917] inline-flex items-center gap-1" data-testid="admin-logout"><LogOut size={12} /> Sign out</button>
      </div>
    </aside>
  );
}

export default function Admin() {
  const { user, loading, login, logout } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[#57534E]">Loading…</div>;
  if (!user) {
    return (
      <div className="min-h-screen ankara flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center bg-white border border-[#EAD9B0] rounded-3xl p-10">
          <img src="/logo.png" alt="" className="h-24 mx-auto" />
          <h1 className="font-display text-2xl mt-4">Staff sign-in</h1>
          <button onClick={login} className="mt-6 btn-primary w-full py-3 rounded-full text-sm font-semibold" data-testid="admin-signin-btn">Continue with Google</button>
        </div>
      </div>
    );
  }
  if (!["admin", "staff", "vet"].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div>
          <h1 className="font-display text-2xl">Staff area</h1>
          <p className="mt-2 text-sm text-[#57534E]">You don't have staff access.</p>
          <Link to="/portal" className="mt-6 inline-block btn-primary px-6 py-2.5 rounded-full text-sm font-semibold">Go to client portal</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ankara lg:flex" data-testid="admin-shell">
      <Sidebar user={user} logout={logout} />
      <main className="flex-1 min-w-0">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="consultations" element={<Consultations />} />
          <Route path="farm-visits" element={<FarmVisits />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="appointments" element={<Appointments />} />
          <Route path="services" element={<Services />} />
          <Route path="products" element={<Products />} />
          <Route path="clients" element={<Clients />} />
          <Route path="messages" element={<Messages />} />
          <Route path="settings" element={<SiteSettings />} />
        </Routes>
      </main>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [appts, setAppts] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/appointments").then((r) => setAppts(r.data));
  }, []);
  const today = new Date().toISOString().slice(0, 10);
  const todays = appts.filter((a) => a.start_at.startsWith(today) && a.status === "confirmed");
  const cards = [
    ["New Consultations", stats?.new_consultations ?? 0, "#C2410C"],
    ["Today", stats?.today_appointments ?? 0, "#0F766E"],
    ["Upcoming", stats?.upcoming_appointments ?? 0, "#0F766E"],
    ["Clients", stats?.clients ?? 0, "#D4A017"],
    ["Pets", stats?.pets ?? 0, "#0F766E"],
    ["Products", stats?.products ?? 0, "#C2410C"],
    ["Services", stats?.services ?? 0, "#0F766E"],
    ["Unread", stats?.unread_messages ?? 0, "#D4A017"],
  ];
  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="admin-overview">
      <h1 className="font-display text-2xl md:text-3xl mb-6 md:mb-8">Overview</h1>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4 mb-8">
        {cards.map(([label, value, color]) => (
          <div key={label} className="bg-white border border-[#EAD9B0] rounded-2xl p-4" data-testid={`stat-${label.toLowerCase()}`}>
            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>{label}</div>
            <div className="font-display text-2xl mt-1">{value}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-[#EAD9B0] rounded-2xl p-6">
        <h2 className="font-display text-xl mb-4">Today's schedule</h2>
        {todays.length === 0 && <p className="text-sm text-[#57534E]">No appointments scheduled for today.</p>}
        <div className="space-y-2">
          {todays.map((a) => (
            <div key={a.appointment_id} className="flex justify-between text-sm border-b border-[#F5EDD9] py-2">
              <div><b>{new Date(a.start_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</b> · {a.service_name} · {a.pet_name}</div>
              <div className="text-[#57534E]">{a.client_name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const STATUSES = ["New", "Under Review", "Contacted", "Appointment Required", "In Progress", "Completed", "Cancelled"];

function Consultations() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("All");
  const load = () => api.get("/consultations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const update = async (cid, patch) => { await api.put(`/consultations/${cid}`, patch); await load();
    if (selected?.consultation_id === cid) setSelected({ ...selected, ...patch }); toast.success("Saved"); };
  const visible = filter === "All" ? items : items.filter((c) => c.status === filter);
  return (
    <div className="p-6 lg:p-10" data-testid="admin-consultations">
      <h1 className="font-display text-3xl mb-6">Consultations</h1>
      <div className="flex flex-wrap gap-2 mb-6">
        {["All", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${filter === s ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white border-[#EAD9B0] hover:border-[#0F766E]"}`} data-testid={`cf-${s}`}>{s}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-2 max-h-[80vh] overflow-auto">
          {visible.map((c) => (
            <button key={c.consultation_id} onClick={() => setSelected(c)}
              className={`w-full text-left p-3 rounded-xl border transition-colors ${selected?.consultation_id === c.consultation_id ? "border-[#0F766E] bg-white" : "border-[#EAD9B0] bg-white/60 hover:bg-white"}`}
              data-testid={`consult-item-${c.consultation_id}`}>
              <div className="flex justify-between items-start">
                <div className="font-display text-sm">{c.owner_name} · {c.pet_name}</div>
                <span className="chip chip-emerald text-[9px]">{c.status}</span>
              </div>
              <div className="text-[11px] text-[#57534E] mt-1">{c.animal_type} · {new Date(c.created_at).toLocaleString()}</div>
              <div className="text-xs text-[#57534E] mt-1 line-clamp-2">{c.reason}</div>
            </button>
          ))}
          {visible.length === 0 && <p className="text-sm text-[#57534E]">No consultations.</p>}
        </div>
        <div className="lg:col-span-2 bg-white border border-[#EAD9B0] rounded-2xl p-6 min-h-[400px]">
          {!selected ? <p className="text-sm text-[#57534E]">Select a consultation to view details.</p> : (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-display text-2xl">{selected.pet_name} <span className="text-[#57534E] font-normal">({selected.animal_type}{selected.animal_type_other ? ` - ${selected.animal_type_other}` : ""})</span></h2>
                  <div className="text-sm text-[#57534E]">Owner: <b>{selected.owner_name}</b> · {selected.owner_phone}{selected.owner_email ? ` · ${selected.owner_email}` : ""}</div>
                </div>
                <select value={selected.status} onChange={(e) => update(selected.consultation_id, { status: e.target.value })}
                  data-testid="consult-status" className="rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-2 text-sm">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <Meta k="Breed" v={selected.breed} /><Meta k="Age" v={selected.age} />
                <Meta k="Sex" v={selected.sex} /><Meta k="Prior treatment" v={selected.prior_treatment} />
              </div>
              <Meta k="Reason for consultation" v={selected.reason} block />
              {selected.problem_start && <Meta k="When did it start" v={selected.problem_start} block />}
              {selected.prior_treatment === "Yes" && <Meta k="Prior treatment details" v={selected.prior_treatment_details} block />}
              {selected.file_ids?.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#57534E] mb-2">Attached files</div>
                  <ul className="space-y-1 text-sm">{selected.file_ids.map((f) => <li key={f} className="text-[#57534E]">📎 {f}</li>)}</ul>
                </div>
              )}
              <div className="mt-6">
                <label className="text-[10px] uppercase tracking-widest text-[#57534E]">Internal notes</label>
                <textarea rows={4} defaultValue={selected.internal_notes || ""} onBlur={(e) => update(selected.consultation_id, { internal_notes: e.target.value })}
                  data-testid="consult-notes" className="mt-2 w-full rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 text-sm" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function Meta({ k, v, block }) {
  return block ? (
    <div className="mt-4"><div className="text-[10px] uppercase tracking-widest text-[#57534E] mb-1">{k}</div><p className="text-sm whitespace-pre-line">{v || "—"}</p></div>
  ) : (
    <div><span className="text-[10px] uppercase tracking-widest text-[#57534E]">{k}: </span><span className="font-medium">{v || "—"}</span></div>
  );
}

function Calendar() {
  const [appts, setAppts] = useState([]);
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  useEffect(() => { api.get("/appointments").then((r) => setAppts(r.data.filter((a) => a.status === "confirmed"))); }, []);
  const days = useMemo(() => {
    const d = new Date(anchor);
    if (view === "day") return [new Date(d)];
    if (view === "week") {
      const s = new Date(d); s.setDate(d.getDate() - d.getDay());
      return Array.from({ length: 7 }, (_, i) => { const x = new Date(s); x.setDate(s.getDate() + i); return x; });
    }
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const grid = []; const off = first.getDay();
    const di = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    for (let i = 0; i < off; i++) grid.push(null);
    for (let i = 1; i <= di; i++) grid.push(new Date(d.getFullYear(), d.getMonth(), i));
    return grid;
  }, [anchor, view]);
  const col = () => "#0F766E";
  return (
    <div className="p-6 lg:p-10" data-testid="admin-calendar">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-display text-3xl">Calendar</h1>
        <div className="flex gap-2">
          {["day", "week", "month"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
              className={`px-3 py-1.5 rounded-full text-xs capitalize ${view === v ? "bg-[#0F766E] text-white" : "bg-white border border-[#EAD9B0]"}`}>{v}</button>
          ))}
          <button onClick={() => { const d = new Date(anchor); d.setDate(d.getDate() - (view === "month" ? 30 : view === "week" ? 7 : 1)); setAnchor(d); }} className="px-3 py-1.5 rounded-full text-xs bg-white border border-[#EAD9B0]">‹</button>
          <button onClick={() => setAnchor(new Date())} className="px-3 py-1.5 rounded-full text-xs bg-white border border-[#EAD9B0]">Today</button>
          <button onClick={() => { const d = new Date(anchor); d.setDate(d.getDate() + (view === "month" ? 30 : view === "week" ? 7 : 1)); setAnchor(d); }} className="px-3 py-1.5 rounded-full text-xs bg-white border border-[#EAD9B0]">›</button>
        </div>
      </div>
      <div className="bg-white border border-[#EAD9B0] rounded-2xl overflow-hidden">
        {view !== "month" ? (
          <div className="grid" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
            {days.map((day) => {
              const iso = day.toISOString().slice(0, 10);
              const dayAppts = appts.filter((a) => a.start_at.startsWith(iso)).sort((a, b) => a.start_at.localeCompare(b.start_at));
              return (
                <div key={iso} className="p-3 border-r last:border-r-0 border-[#F5EDD9] min-h-[300px]">
                  <div className="text-[10px] uppercase tracking-widest text-[#57534E]">{day.toLocaleDateString([], { weekday: "short" })}</div>
                  <div className="font-display text-2xl">{day.getDate()}</div>
                  <div className="mt-3 space-y-2">
                    {dayAppts.map((a) => (
                      <div key={a.appointment_id} className="p-2 rounded-lg text-xs" style={{ background: `${col()}18`, borderLeft: `3px solid ${col()}` }}>
                        <div className="font-medium">{new Date(a.start_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
                        <div className="text-[#57534E] truncate">{a.service_name}</div>
                        <div className="text-[#57534E] truncate">{a.pet_name} · {a.client_name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-7 text-xs">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="p-2 font-semibold border-b border-[#EAD9B0] text-[#57534E]">{d}</div>)}
            {days.map((d, i) => {
              if (!d) return <div key={i} className="p-3 border-b border-r border-[#F5EDD9] min-h-[100px]" />;
              const iso = d.toISOString().slice(0, 10);
              const c = appts.filter((a) => a.start_at.startsWith(iso)).length;
              return (
                <div key={iso} className="p-2 border-b border-r border-[#F5EDD9] min-h-[100px]">
                  <div className="font-medium">{d.getDate()}</div>
                  {c > 0 && <div className="mt-1 text-[10px] px-2 py-0.5 rounded-full inline-block bg-[#0F766E]/15 text-[#0F766E]">{c} appt</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Appointments() {
  const [appts, setAppts] = useState([]);
  const [editing, setEditing] = useState(null); // appointment_id being edited for meeting link
  const [linkInput, setLinkInput] = useState("");
  const load = () => api.get("/appointments").then((r) => setAppts(r.data));
  useEffect(() => { load(); }, []);
  const saveLink = async (aid) => {
    if (!linkInput.trim()) return toast.error("Enter a meeting link");
    try {
      await api.put(`/appointments/${aid}/meeting`, { meeting_link: linkInput.trim(), meeting_provider: "GoWorldMet" });
      toast.success("Meeting link saved and emailed to client");
      setEditing(null); setLinkInput(""); load();
    } catch { toast.error("Failed to save link"); }
  };
  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="admin-appointments">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Appointments</h1>
        <a href="https://goworldmet.forum/" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold" data-testid="open-goworldmet">
          📹 Open GoWorldMet
        </a>
      </div>
      <div className="bg-white border border-[#EAD9B0] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#FFF7E6] text-left text-[10px] uppercase tracking-widest text-[#57534E]">
            <tr><th className="p-3">When</th><th className="p-3">Client</th><th className="p-3">Pet</th><th className="p-3">Service</th><th className="p-3">Mode</th><th className="p-3">Meeting</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {appts.map((a) => (
              <tr key={a.appointment_id} className="border-t border-[#F5EDD9]" data-testid={`admin-appt-${a.appointment_id}`}>
                <td className="p-3">{new Date(a.start_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                <td className="p-3">{a.client_name}<div className="text-[10px] text-[#57534E]">{a.client_email}</div></td>
                <td className="p-3">{a.pet_name}</td>
                <td className="p-3">{a.service_name}</td>
                <td className="p-3 capitalize">{a.mode}</td>
                <td className="p-3">
                  {a.mode === "virtual" ? (
                    editing === a.appointment_id ? (
                      <div className="flex gap-1">
                        <input value={linkInput} onChange={(e) => setLinkInput(e.target.value)} placeholder="Paste GoWorldMet link"
                          className="w-52 rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] px-2 py-1 text-xs" data-testid={`link-input-${a.appointment_id}`} autoFocus />
                        <button onClick={() => saveLink(a.appointment_id)} className="text-xs text-[#0F766E] hover:underline" data-testid={`save-link-${a.appointment_id}`}>Save</button>
                        <button onClick={() => { setEditing(null); setLinkInput(""); }} className="text-xs text-[#57534E]">Cancel</button>
                      </div>
                    ) : a.meeting_link ? (
                      <div className="flex gap-2 items-center">
                        <a href={a.meeting_link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0F766E] hover:underline">📹 Open</a>
                        <button onClick={() => { setEditing(a.appointment_id); setLinkInput(a.meeting_link); }} className="text-xs text-[#57534E] hover:text-[#1C1917]" data-testid={`edit-link-${a.appointment_id}`}>Edit</button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditing(a.appointment_id); setLinkInput(""); }} className="text-xs text-[#0F766E] hover:underline" data-testid={`add-link-${a.appointment_id}`}>+ Add link</button>
                    )
                  ) : <span className="text-[10px] text-[#57534E]">—</span>}
                </td>
                <td className="p-3 capitalize">{a.status}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {a.status === "confirmed" && (<>
                    <button onClick={async () => { await api.post(`/appointments/${a.appointment_id}/complete`); load(); toast.success("Completed"); }} className="text-xs text-[#0F766E] hover:underline mr-3">Complete</button>
                    <button onClick={async () => { await api.post(`/appointments/${a.appointment_id}/cancel`); load(); toast.success("Cancelled"); }} className="text-xs text-red-600 hover:underline">Cancel</button>
                  </>)}
                </td>
              </tr>
            ))}
            {appts.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#57534E]">No appointments yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-[#57534E]">💡 For virtual appointments: tap <b>Open GoWorldMet</b>, create a new meeting, copy the room link, then paste it into the Meeting column. The client receives an email with a "Join Video Call" button.</p>
    </div>
  );
}

function Services() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", category: "Consultation", description: "", duration_min: 30, price: 0, price_display: "", deposit_required: false, online_bookable: true, active: true, mode: "in-person" });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/services/all").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  if (user.role !== "admin") return <div className="p-10 text-sm text-[#57534E]">Only super admins can manage services.</div>;
  const save = async () => {
    try { if (editing) await api.put(`/services/${editing}`, form); else await api.post("/services", form);
      setForm({ name: "", category: "Consultation", description: "", duration_min: 30, price: 0, price_display: "", deposit_required: false, online_bookable: true, active: true, mode: "in-person" });
      setEditing(null); load(); toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };
  return (
    <div className="p-6 lg:p-10" data-testid="admin-services">
      <h1 className="font-display text-3xl mb-6">Services</h1>
      <div className="bg-white border border-[#EAD9B0] rounded-2xl p-5 grid grid-cols-1 md:grid-cols-4 gap-3 mb-8">
        <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INP} data-testid="svc-name" />
        <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={INP} data-testid="svc-cat" />
        <input type="number" placeholder="Duration (min)" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value || 0) })} className={INP} data-testid="svc-dur" />
        <input type="number" placeholder="Price (₦)" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || 0) })} className={INP} data-testid="svc-price" />
        <input placeholder='Price display (e.g. "Contact for Price")' value={form.price_display} onChange={(e) => setForm({ ...form, price_display: e.target.value })} className={INP + " md:col-span-2"} data-testid="svc-price-disp" />
        <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={INP} data-testid="svc-mode">
          <option value="in-person">In-person</option><option value="virtual">Virtual</option><option value="both">Both</option>
        </select>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="svc-active" /> Active</label>
        <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={INP + " md:col-span-4"} rows={2} data-testid="svc-desc" />
        <button onClick={save} className="md:col-span-4 btn-primary py-2.5 rounded-full text-sm font-semibold" data-testid="svc-save">{editing ? "Update" : "Add"} service</button>
      </div>
      <div className="bg-white border border-[#EAD9B0] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FFF7E6] text-left text-[10px] uppercase tracking-widest text-[#57534E]">
            <tr><th className="p-3">Name</th><th className="p-3">Category</th><th className="p-3">Duration</th><th className="p-3">Price</th><th className="p-3">Active</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.service_id} className="border-t border-[#F5EDD9]">
                <td className="p-3">{s.name}</td><td className="p-3">{s.category}</td><td className="p-3">{s.duration_min} min</td>
                <td className="p-3">{s.price_display || `₦${s.price.toLocaleString()}`}</td>
                <td className="p-3">{s.active ? "Yes" : "No"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => { setEditing(s.service_id); setForm({ name: s.name, category: s.category, description: s.description, duration_min: s.duration_min, price: s.price, price_display: s.price_display || "", deposit_required: s.deposit_required, online_bookable: s.online_bookable, active: s.active, mode: s.mode || "in-person" }); }} className="text-xs text-[#0F766E] hover:underline mr-3">Edit</button>
                  <button onClick={async () => { if (confirm("Delete?")) { await api.delete(`/services/${s.service_id}`); load(); } }} className="text-xs text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Products() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", category: "Dog Food", description: "", price: 0, price_display: "", stock: 0, prescription_required: false, image_url: "", active: true });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/products/all").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  if (user.role !== "admin") return <div className="p-10 text-sm text-[#57534E]">Only super admins can manage products.</div>;
  const save = async () => {
    try { if (editing) await api.put(`/products/${editing}`, form); else await api.post("/products", form);
      setForm({ name: "", category: "Dog Food", description: "", price: 0, price_display: "", stock: 0, prescription_required: false, image_url: "", active: true });
      setEditing(null); load(); toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };
  return (
    <div className="p-6 lg:p-10" data-testid="admin-products">
      <h1 className="font-display text-3xl mb-6">Products</h1>
      <div className="bg-white border border-[#EAD9B0] rounded-2xl p-5 grid grid-cols-1 md:grid-cols-4 gap-3 mb-8">
        <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INP} data-testid="prd-name" />
        <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={INP} data-testid="prd-cat" />
        <input type="number" placeholder="Price (₦)" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || 0) })} className={INP} data-testid="prd-price" />
        <input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value || 0) })} className={INP} data-testid="prd-stock" />
        <input placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={INP + " md:col-span-3"} data-testid="prd-image" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.prescription_required} onChange={(e) => setForm({ ...form, prescription_required: e.target.checked })} data-testid="prd-rx" /> Prescription required</label>
        <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={INP + " md:col-span-4"} rows={2} data-testid="prd-desc" />
        <button onClick={save} className="md:col-span-4 btn-primary py-2.5 rounded-full text-sm font-semibold" data-testid="prd-save">{editing ? "Update" : "Add"} product</button>
      </div>
      <div className="bg-white border border-[#EAD9B0] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FFF7E6] text-left text-[10px] uppercase tracking-widest text-[#57534E]">
            <tr><th className="p-3">Name</th><th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3">Rx</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.product_id} className="border-t border-[#F5EDD9]">
                <td className="p-3">{s.name}</td><td className="p-3">{s.category}</td>
                <td className="p-3">₦{s.price.toLocaleString()}</td><td className="p-3">{s.stock}</td>
                <td className="p-3">{s.prescription_required ? "Yes" : "—"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => { setEditing(s.product_id); setForm({ ...s }); }} className="text-xs text-[#0F766E] hover:underline mr-3">Edit</button>
                  <button onClick={async () => { if (confirm("Delete?")) { await api.delete(`/products/${s.product_id}`); load(); } }} className="text-xs text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Clients() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/clients").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const changeRole = async (uid, role) => {
    try { await api.put(`/admin/users/${uid}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const filtered = q ? items.filter((c) => (c.name + c.email).toLowerCase().includes(q.toLowerCase())) : items;
  const canManageRoles = user.role === "admin";
  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="admin-clients">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-2xl md:text-3xl">Users &amp; Clients</h1>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" data-testid="user-search"
          className="rounded-full border border-[#EAD9B0] bg-white px-4 py-2 text-sm w-full sm:w-64" />
      </div>
      <div className="bg-white border border-[#EAD9B0] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#FFF7E6] text-left text-[10px] uppercase tracking-widest text-[#57534E]">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Pets</th><th className="p-3">Visits</th><th className="p-3">Joined</th>{canManageRoles && <th className="p-3">Manage</th>}</tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.user_id} className="border-t border-[#F5EDD9]" data-testid={`user-row-${c.user_id}`}>
                <td className="p-3">{c.name || "—"}</td>
                <td className="p-3 text-xs">{c.email}</td>
                <td className="p-3"><span className={`chip ${c.role === 'admin' ? 'chip-terra' : c.role === 'vet' || c.role === 'staff' ? 'chip-emerald' : 'chip-gold'}`}>{c.role}</span></td>
                <td className="p-3">{c.pet_count}</td><td className="p-3">{c.appointment_count}</td>
                <td className="p-3 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                {canManageRoles && (
                  <td className="p-3">
                    <select value={c.role} onChange={(e) => changeRole(c.user_id, e.target.value)} data-testid={`role-${c.user_id}`}
                      className="rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] px-2 py-1 text-xs">
                      <option value="client">client</option>
                      <option value="staff">staff</option>
                      <option value="vet">vet</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={canManageRoles ? 7 : 6} className="p-6 text-center text-[#57534E]">No users found.</td></tr>}
          </tbody>
        </table>
      </div>
      {canManageRoles && (
        <p className="mt-4 text-xs text-[#57534E]">Emails containing <b>aromolaran</b> or matching the owner email are auto-promoted to admin on first sign-in.</p>
      )}
    </div>
  );
}

function Messages() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/messages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="p-6 lg:p-10" data-testid="admin-messages">
      <h1 className="font-display text-3xl mb-6">Messages</h1>
      <div className="space-y-3">
        {items.map((m) => (
          <div key={m.message_id} className={`bg-white border rounded-2xl p-5 ${m.read ? "border-[#EAD9B0]" : "border-[#0F766E]"}`}>
            <div className="flex justify-between text-xs text-[#57534E] mb-2">
              <span>{m.from_name} &lt;{m.from_email}&gt;{m.phone ? ` · ${m.phone}` : ""}</span>
              <span>{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <div className="font-display text-lg">{m.subject}</div>
            <p className="mt-2 text-sm whitespace-pre-line">{m.body}</p>
            {!m.read && <button onClick={async () => { await api.post(`/messages/${m.message_id}/read`); load(); }} className="mt-3 text-xs text-[#0F766E] hover:underline">Mark as read</button>}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[#57534E]">No messages yet.</p>}
      </div>
    </div>
  );
}

function SiteSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (user.role !== "admin") return <div className="p-10 text-sm text-[#57534E]">Only super admins can edit settings.</div>;
  if (!s) return <div className="p-10 text-sm text-[#57534E]">Loading…</div>;
  const save = async () => { await api.put("/settings", s); toast.success("Settings saved"); };
  const F = ({ k, label, block, textarea }) => (
    <div className={block ? "md:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-[#57534E]">{label}</label>
      {textarea ? (
        <textarea rows={3} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={INP + " mt-2"} data-testid={`set-${k}`} />
      ) : (
        <input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={INP + " mt-2"} data-testid={`set-${k}`} />
      )}
    </div>
  );
  return (
    <div className="p-6 lg:p-10" data-testid="admin-settings">
      <h1 className="font-display text-3xl mb-6">Site Settings</h1>
      <div className="bg-white border border-[#EAD9B0] rounded-2xl p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <F k="business_name" label="Business Name" />
        <F k="owner_name" label="Owner / Lead Vet" />
        <F k="owner_qualification" label="Owner qualification" block />
        <F k="phone" label="Phone" />
        <F k="whatsapp" label="WhatsApp number" />
        <F k="email" label="Email (optional)" />
        <F k="address" label="Address" />
        <F k="hours" label="Business hours" textarea />
        <F k="emergency_notice" label="Emergency notice" textarea block />
        <F k="social_facebook" label="Facebook URL" />
        <F k="social_instagram" label="Instagram URL" />
        <F k="owner_photo_url" label="Owner photo URL (upload Dr. Juwon's photo)" block />
        <button onClick={save} className="md:col-span-2 btn-primary py-3 rounded-full text-sm font-semibold" data-testid="settings-save">Save settings</button>
      </div>
    </div>
  );
}

const INP = "w-full rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-2.5 text-sm";


const FARM_STATUSES = ["New", "Quoted", "Scheduled", "In Progress", "Completed", "Cancelled"];

function FarmVisits() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/farm-visits").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const update = async (vid, patch) => {
    await api.put(`/farm-visits/${vid}`, patch); await load();
    if (selected?.visit_id === vid) setSelected({ ...selected, ...patch });
    toast.success("Saved");
  };
  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="admin-farm-visits">
      <h1 className="font-display text-2xl md:text-3xl mb-6">Farm Visit Requests</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-2 max-h-[80vh] overflow-auto">
          {items.map((v) => (
            <button key={v.visit_id} onClick={() => setSelected(v)}
              className={`w-full text-left p-3 rounded-xl border transition-colors ${selected?.visit_id === v.visit_id ? "border-[#0F766E] bg-white" : "border-[#EAD9B0] bg-white/60 hover:bg-white"}`}
              data-testid={`farm-item-${v.visit_id}`}>
              <div className="flex justify-between items-start">
                <div className="font-display text-sm">{v.owner_name} · {v.farm_name || "Farm"}</div>
                <span className="chip chip-emerald text-[9px]">{v.status}</span>
              </div>
              <div className="text-[11px] text-[#57534E] mt-1">{v.species?.join(", ")} · {v.herd_size} animals</div>
              <div className="text-xs text-[#57534E] mt-1 line-clamp-2">{v.visit_type} · {v.urgency}</div>
              <div className="text-[10px] text-[#8A8074] mt-1">{new Date(v.created_at).toLocaleString()}</div>
            </button>
          ))}
          {items.length === 0 && <p className="text-sm text-[#57534E]">No farm visit requests yet.</p>}
        </div>
        <div className="lg:col-span-2 bg-white border border-[#EAD9B0] rounded-2xl p-6 min-h-[400px]">
          {!selected ? <p className="text-sm text-[#57534E]">Select a request to view details.</p> : (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-display text-2xl">{selected.farm_name || "Farm visit"} <span className="text-[#57534E] font-normal">— {selected.owner_name}</span></h2>
                  <div className="text-sm text-[#57534E]">{selected.owner_phone}{selected.owner_email ? ` · ${selected.owner_email}` : ""}</div>
                </div>
                <select value={selected.status} onChange={(e) => update(selected.visit_id, { status: e.target.value })}
                  data-testid="farm-status" className="rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-2 text-sm">
                  {FARM_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[10px] uppercase tracking-widest text-[#57534E]">Visit type: </span><b>{selected.visit_type}</b></div>
                <div><span className="text-[10px] uppercase tracking-widest text-[#57534E]">Urgency: </span><b>{selected.urgency}</b></div>
                <div><span className="text-[10px] uppercase tracking-widest text-[#57534E]">Herd: </span><b>{selected.herd_size}</b></div>
                <div><span className="text-[10px] uppercase tracking-widest text-[#57534E]">Species: </span><b>{selected.species?.join(", ")}{selected.species_other ? ` (${selected.species_other})` : ""}</b></div>
                <div><span className="text-[10px] uppercase tracking-widest text-[#57534E]">Preferred date: </span>{selected.preferred_date || "—"}</div>
                <div>
                  {selected.latitude != null ? (
                    <a href={`https://www.google.com/maps?q=${selected.latitude},${selected.longitude}`} target="_blank" rel="noopener noreferrer" className="text-[#0F766E] underline text-xs" data-testid="farm-map">Open in Google Maps</a>
                  ) : <span className="text-[10px] text-[#57534E]">No GPS pin</span>}
                </div>
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-[#57534E] mb-1">Address</div>
                <p className="text-sm">{selected.address}</p>
              </div>
              {selected.notes && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#57534E] mb-1">Notes</div>
                  <p className="text-sm whitespace-pre-line">{selected.notes}</p>
                </div>
              )}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-[#57534E]">Quote amount (NGN)</label>
                  <input type="number" defaultValue={selected.quote_amount || ""} onBlur={(e) => update(selected.visit_id, { quote_amount: e.target.value ? parseFloat(e.target.value) : null, quote_currency: "NGN" })}
                    data-testid="farm-quote" className="mt-2 w-full rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#57534E]">Scheduled at</label>
                  <input type="datetime-local" defaultValue={selected.scheduled_at || ""} onBlur={(e) => update(selected.visit_id, { scheduled_at: e.target.value || null })}
                    data-testid="farm-scheduled" className="mt-2 w-full rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 text-sm" />
                </div>
              </div>
              <div className="mt-4">
                <label className="text-[10px] uppercase tracking-widest text-[#57534E]">Internal notes</label>
                <textarea rows={4} defaultValue={selected.internal_notes || ""} onBlur={(e) => update(selected.visit_id, { internal_notes: e.target.value })}
                  data-testid="farm-notes" className="mt-2 w-full rounded-lg border border-[#EAD9B0] bg-[#FFF7E6] p-3 text-sm" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
